/** The current page the user is viewing in the HOST platform.
 *
 *  The unified agent runs in a side panel embedded in the ERP platform; the
 *  host knows its own routes, so it is the source of truth for "current page".
 *  The host registers a provider via `setPageContextProvider`; chat-ui forwards
 *  whatever it returns in the turn payload as `page_context`. The chat-ui app
 *  itself is only a test harness — without a registered provider, no page
 *  context is sent (the agent simply has none, same as before this feature).
 */
export interface PageContext {
  /** Host route/path, e.g. "/customers/42". */
  route?: string;
  /** Human label, e.g. "Customer: ACME Corp". */
  label?: string;
  /** Optional entity ids on the page, e.g. { customer_id: "42" }. */
  entities?: Record<string, string>;
}

type Provider = () => PageContext | null | undefined;

let _provider: Provider | null = null;

/** Register (or clear, with null) the host's current-page provider. */
export function setPageContextProvider(provider: Provider | null): void {
  _provider = provider;
}

/** Resolve the current page as a payload fragment: `{ page_context }` when the
 *  host reports a page with a route or label, else `{}`. Never throws — a
 *  misbehaving host provider must not break sending a message. */
export function resolvePageContext(): { page_context?: PageContext } {
  if (!_provider) return {};
  try {
    const pc = _provider();
    if (!pc) return {};
    const hasRoute = typeof pc.route === "string" && pc.route.trim() !== "";
    const hasLabel = typeof pc.label === "string" && pc.label.trim() !== "";
    if (!hasRoute && !hasLabel) return {};
    return { page_context: pc };
  } catch {
    return {};
  }
}
